import { AfterViewInit, Component, HostListener, Injectable, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { cast, IQuestionnaireGroup, Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { DeveloperService } from 'src/app/services/developer/developer.service';
import { WorkflowItemKeyDirective } from '../../../../../directives/workflow-item-key/workflow-item-key.directive';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { isNil } from 'lodash';
import { TappingPenMockService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/tapping-test/tapping-pen-mock.service';
import { TappingPenMonitorService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/tapping-test/tapping-pen-monitor.service';
import { filter, take } from 'rxjs/operators';
import { TappingTestEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/tapping-test/tapping-test-epilog.component';

export type TappingResult = { taps: number }[];
@Injectable()
export class TappingTest implements ISpecialQuestion {
	public readonly component = TappingTestComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'TappingTest';
	public readonly requiredDevices = [Messgeraete.TappingPen];

	title = 'Tapping Test';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: this.title,
		preface: [
			'Im Tapping Test es Ihre Aufgabe, mit dem VITA PEN eine Minute lang so schnell wie möglich auf dem Tisch zu klopfen. ' +
			'Bitte nutzen Sie hierzu Ihre bevorzugte Hand und beachten Sie, dass eine Minute wirklich lang sein kann. ' +
			'Sobald Sie mit dem Klopfen beginnen, wird die Zeit gestartet. ' +
			'Das Ende des Testes wird Ihnen durch den PC signalisiert.'
		],
		items: [cast<BlockQuestionItem>({
			type: 'BlockQuestion',
			title: this.title,
			items: [
				{
					key: '0.taps',
					label: 'Anzahl Testzyklus 1',
				}, {
					key: '1.taps',
					label: 'Anzahl Testzyklus 2',
				}, {
					key: '2.taps',
					label: 'Anzahl Testzyklus 3',
				}, {
					key: '3.taps',
					label: 'Anzahl Testzyklus 4',
				},
			],
			default: cast<RangeQuestion>({
				type: 'range',
				rangeLow: 0,
				rangeHigh: 150,
				steps: 1,
			}),
			isProcedure: true,
		})]
	};

	public isCompleted(currentValue: TappingResult): boolean {
		if (!currentValue) {
			return false;
		}
		for (let i = 0; i < 4; i++) {
			const element = currentValue[i];
			if (!element || isNil(element.taps)) {
				return false;
			}
		}
		return true;
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			suppressDescription: true,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: TappingTestEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

@Component({
	selector: 'workflow-tapping-test',
	templateUrl: './tapping-test.component.html',
	styleUrls: ['./tapping-test.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class TappingTestComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, ISpecialQuestionComponent {

	results: number[];
	isRunning: boolean;
	completed: boolean;
	tappingPenReady = false;
	statusMessage = 'Der Stift ist nicht bereit. Bitte warten Sie, bis der Stift bereit ist.';

	countdownTimer;

	private subscription = new Subscription();
	private isDemoMode = false;

	constructor(
		private workflowItemKey: WorkflowItemKeyDirective,
		devSettings: DeveloperService,
		router: Router,
		tappingPenMonitorService: TappingPenMonitorService,
		private tappingPenMockService: TappingPenMockService,
	) {
		super(router);
		this.subscription.add(devSettings.isDemoMode$.subscribe(isDemoMode => this.isDemoMode = isDemoMode));
		this.subscription.add(tappingPenMonitorService.active$.pipe(
			filter(state => state === false),
			take(1),
		).subscribe(() => {
			if (!this.completed && !this.isRunning) {
				this.tappingPenReady = true;
				this.statusMessage = 'Der Test startet, sobald Sie mit dem Klopfen beginnen.';
			}
		}));
	}

	//Für Testzwecke
	@HostListener('document:keydown', ['$event'])
	async handleKeyboardEvent(event: KeyboardEvent): Promise<void> {
		if (this.isDemoMode && event.code === 'IntlBackslash') {
			await this.tappingPenMockService.runTestProgram({measurementCount: 4, measurementDuration: 1, pauseDuration: 1, log: true });
		}
	}

	@HostListener('window:vitapen-test-start', ['$event'])
	vitaPenTestStart(): void {
		if (this.isRunning || this.completed) {
			return;
		}
		this.results = [];
		this.statusMessage = 'Der Test wurde gestartet. Es kann nun geklopft werden.';
		this.isRunning = true;
	}

	@HostListener('window:vitapen-test-results', ['$event'])
	vitaPenTestResults(event: CustomEvent): void {
		if (!this.isRunning) {
			return;
		}
		if (event?.detail?.results) {
			this.results = event.detail.results;
		}
	}

	@HostListener('window:vitapen-test-end', ['$event'])
	vitaPenTestEnd(): void {
		if (!this.isRunning) {
			return;
		}
		this.isRunning = false;
		this.statusMessage = 'Der Test wurde beendet. Stellen Sie das Klopfen bitte ein.';
		this.resultsUpdated();
		this.valid.emit(true);
	}

	ngOnInit(): void {
		this.isRunning = false;
		this.valid.emit(false);

		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data && typeof(data[0]) === 'number' ? data as number[] : undefined;
				if (this.results) {
					this.valid.emit(true);
				}
			}
		}));
	}

	ngOnDestroy(): void {
		clearInterval(this.countdownTimer);
		this.subscription.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.results = [];
	}

	resultsUpdated(): void {
		if (this.question?.key && this.results.length && this.workflowItemKey) {
			const sendResults: TappingResult = [];

			this.results.forEach(cycle => {
				sendResults.push({ taps: cycle });
			});

			if (!this.isRunning) {
				this.completed = true;
				this.workflowItemKey.setProperty(sendResults);
			}
		}
	}

}
